<div class="legal-page">
  <article class="legal-document">
    <header>
      <h1>End User License Agreement</h1>
      <p class="effective">Effective {{ effectiveDate }}</p>
    </header>

    <p class="intro">
      This End User License Agreement ("Agreement") is a binding agreement between
      <strong>BusinessHub LLC</strong> ("BusinessHub", "we", "us") and the organisation
      or individual accessing the BusinessHub platform ("Customer", "you"). By creating an
      account, accessing, or using the platform, you agree to these terms. If you are
      entering into this Agreement on behalf of an organisation, you represent that you have
      authority to bind that organisation.
    </p>

    <h2>1. The Service</h2>
    <p>
      BusinessHub is a financial planning and analysis platform that allows you to connect
      data sources, define datasets and metrics, build dashboards and reports, and share
      them within your organisation. We grant you a non-exclusive, non-transferable,
      revocable licence to access and use the platform for your internal business purposes
      for as long as this Agreement remains in effect.
    </p>

    <h2>2. Accounts and Access</h2>
    <p>
      Access is granted per application and per user. Users are assigned roles that determine
      what they may see and change; owner and administrator roles can configure integrations,
      manage security, and invite or remove other users. You are responsible for maintaining
      the confidentiality of account credentials, for the actions taken under your accounts,
      and for ensuring that access is removed promptly when a user should no longer have it.
    </p>

    <h2>3. Your Data</h2>
    <p>
      You retain all right, title, and interest in the data you upload, enter, or connect to
      the platform ("Customer Data"). We claim no ownership of it. You grant us a limited
      licence to host, process, transmit, and display Customer Data solely as necessary to
      provide the platform to you, to maintain and improve its reliability and security, and
      as otherwise permitted by our
      <a routerLink="/privacy">Privacy Policy</a>.
    </p>
    <p>
      You are responsible for ensuring you have the rights necessary to provide Customer Data
      to us, including any personal data belonging to your employees, customers, or others,
      and for using the platform in compliance with the laws that apply to you.
    </p>

    <h2>4. Third-Party Integrations</h2>
    <p>
      The platform can connect to third-party services, including accounting systems such as
      Intuit QuickBooks Online, and to databases and storage services you control. When you
      authorise an integration, you direct us to access that service on your behalf using the
      permissions you approve.
    </p>
    <p>
      Third-party services are governed by their own terms and privacy practices, and we are
      not responsible for their availability, accuracy, or conduct. You may revoke an
      integration at any time from within the platform, and doing so ends our access to that
      service. Data already retrieved and stored in your destination database remains there
      unless you remove it.
    </p>

    <h2>5. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>use the platform in violation of any applicable law or regulation;</li>
      <li>upload material that infringes the rights of others, or that contains malicious code;</li>
      <li>attempt to gain unauthorised access to the platform, other customers' data, or the
          underlying infrastructure;</li>
      <li>probe, scan, or test the vulnerability of the platform without our prior written consent;</li>
      <li>resell, sublicense, or make the platform available to third parties outside your
          organisation, except as expressly permitted in writing;</li>
      <li>reverse engineer or attempt to derive source code, except to the extent that
          restriction is prohibited by law.</li>
    </ul>

    <h2>6. Fees</h2>
    <p>
      Fees, billing frequency, and any usage limits are set out in the order form or written
      agreement between you and us. Unless stated otherwise there, fees are non-refundable,
      and unpaid amounts may result in suspension of access after reasonable notice.
    </p>

    <h2>7. Confidentiality</h2>
    <p>
      Each party may receive information from the other that is confidential. Each party
      agrees to protect the other's confidential information with at least the same care it
      uses for its own, and not to disclose it except to those who need it to perform under
      this Agreement and who are bound by comparable obligations. This does not apply to
      information that is public through no fault of the receiving party, was already known
      to it, or is independently developed.
    </p>

    <h2>8. Availability and Support</h2>
    <p>
      We aim to keep the platform available and to provide support as described in any
      applicable service level or support terms. The platform may be unavailable during
      planned maintenance or because of events outside our reasonable control. Except where a
      separate service level agreement applies, the platform is provided without an uptime
      commitment.
    </p>

    <h2>9. Disclaimers</h2>
    <p>
      Except as expressly stated in this Agreement, the platform is provided "as is" and "as
      available", without warranties of any kind, whether express, implied, or statutory,
      including implied warranties of merchantability, fitness for a particular purpose, and
      non-infringement. <strong>The platform assists with financial analysis; it does not
      provide accounting, tax, legal, or investment advice, and outputs should be reviewed by
      a qualified professional before being relied upon.</strong>
    </p>

    <h2>10. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, neither party is liable for indirect,
      incidental, special, consequential, or punitive damages, or for lost profits, revenue,
      or data, even if advised of the possibility. Our total aggregate liability arising out
      of or related to this Agreement will not exceed the amounts paid by you to us in the
      twelve months preceding the event giving rise to the claim. These limits do not apply
      to liability that cannot be limited by law.
    </p>

    <h2>11. Indemnification</h2>
    <p>
      You agree to defend and indemnify us against third-party claims arising from your
      Customer Data or your use of the platform in breach of this Agreement, and we agree to
      defend and indemnify you against third-party claims that the platform, used as
      permitted, infringes that third party's intellectual property rights.
    </p>

    <h2>12. Term and Termination</h2>
    <p>
      This Agreement continues until terminated. Either party may terminate for material
      breach that remains uncured thirty (30) days after written notice. On termination your
      right to access the platform ends. You may export Customer Data before termination, and
      we will delete or return it in accordance with our
      <a routerLink="/privacy">Privacy Policy</a>. Sections that by their nature should
      survive termination will do so.
    </p>

    <h2>13. Changes to this Agreement</h2>
    <p>
      We may update this Agreement from time to time. If we make material changes we will
      provide reasonable notice, such as by email or an in-product notice, before they take
      effect. Continuing to use the platform after changes take effect means you accept them.
    </p>

    <h2>14. Governing Law</h2>
    <p>
      This Agreement is governed by the laws of the <strong>State of Illinois</strong>,
      without regard to its conflict of law rules. The parties submit to the exclusive
      jurisdiction of the state and federal courts located in
      <strong>Cook County, Illinois</strong>.
    </p>

    <h2>15. Contact</h2>
    <address>
      <strong>BusinessHub LLC</strong><br />
      Chicago, IL<br />
      <a href="mailto:info&#64;businesshubapp.com">info&#64;businesshubapp.com</a><br />
      +1 (708) 220-9276
    </address>

    <footer>
      <p>Last updated {{ effectiveDate }}.</p>
      <p><a routerLink="/privacy">Privacy Policy</a></p>
    </footer>
  </article>
</div>
